import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle, RefreshCw, X } from "lucide-react";
import { resolveBackendUrl } from "../lib/backendUrl";

/* On app mount, verify the frontend build is pointing at a reachable, real
   VetSim backend. If the ping fails (no response, timeout, non-JSON page,
   wrong shape), render a fixed banner so the failure mode is obvious even
   to a non-technical user. Historically this class of failure showed only
   as "couldn't reach the server" from a login attempt — a passive banner
   surfaces the issue before the user even clicks anything.

   Auto-retries on transient failures (network blip, cold-start latency,
   ad-blocker race). Only paints the banner after N failed attempts, and
   exposes a manual Retry button so users don't need a full page reload.

   Failure modes this catches:
   - REACT_APP_BACKEND_URL is missing / undefined
   - REACT_APP_BACKEND_URL points at a host that no longer exists (DNS fail)
   - REACT_APP_BACKEND_URL points at some other server (returns HTML, not JSON)
   - Backend pod is down / ingress is misconfigured (5xx from /api/health) */

const BACKEND_URL = resolveBackendUrl();
const AUTO_RETRIES = 3; // total attempts before the banner appears
const RETRY_DELAY_MS = 1500;

async function pingHealth() {
  if (!BACKEND_URL) return { ok: false, reason: "REACT_APP_BACKEND_URL is not set" };
  try {
    const res = await axios.get(`${BACKEND_URL}/api/health`, {
      timeout: 8000,
      headers: { Accept: "application/json" },
      // no credentials — this must never depend on cookies
    });
    const isRealHealth = res?.data && typeof res.data === "object"
      && ("status" in res.data) && ("db" in res.data);
    return isRealHealth
      ? { ok: true, reason: null }
      : { ok: false, reason: "Backend URL is not a VetSim API" };
  } catch (e) {
    const reason =
      e?.code === "ECONNABORTED" ? "Backend request timed out"
      : e?.response?.status ? `Backend returned HTTP ${e.response.status}`
      : "No response from backend (network/CORS/DNS)";
    return { ok: false, reason };
  }
}

export function BackendHealthBanner() {
  const [state, setState] = useState({ checked: false, ok: null, reason: null });
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const cancelledRef = useRef(false);

  const runCheck = useCallback(async (attempts = AUTO_RETRIES) => {
    setBusy(true);
    let last = null;
    for (let i = 0; i < attempts; i += 1) {
      last = await pingHealth();
      if (cancelledRef.current) return;
      if (last.ok) {
        setState({ checked: true, ok: true, reason: null });
        setBusy(false);
        return;
      }
      // Don't retry if the backend actually answered with something meaningful
      // (a real HTTP status or wrong-shape body means it IS reachable — no
      // point pinging again). Only retry on the network/timeout branches.
      const shouldRetry =
        last.reason === "No response from backend (network/CORS/DNS)"
        || last.reason === "Backend request timed out";
      if (!shouldRetry) break;
      if (i < attempts - 1) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        if (cancelledRef.current) return;
      }
    }
    setState({ checked: true, ok: false, reason: last?.reason || "Unknown error" });
    setBusy(false);
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    runCheck();
    // When the browser reports it's come back online, silently re-check the
    // backend. Clears a stuck red banner without requiring a page refresh —
    // and avoids showing the banner for a transient offline blip that the
    // OfflineBanner already surfaced.
    const onOnline = () => { runCheck(); };
    window.addEventListener("online", onOnline);
    return () => {
      cancelledRef.current = true;
      window.removeEventListener("online", onOnline);
    };
  }, [runCheck]);

  // While the browser is offline, defer to OfflineBanner. Otherwise both
  // banners fire simultaneously and it looks like two separate outages.
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  if (offline || !state.checked || state.ok || dismissed) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[9999] bg-rose-600 text-white shadow-lg border-b border-rose-800"
      data-testid="backend-unreachable-banner"
      role="alert"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-semibold">This app can&apos;t reach its backend.</div>
          <div className="text-rose-100/95 mt-0.5">
            Frontend is trying to reach{" "}
            <span
              className="font-mono-clinical bg-rose-800/50 rounded px-1 py-0.5 break-all"
              data-testid="backend-unreachable-url"
            >
              {BACKEND_URL || "(REACT_APP_BACKEND_URL unset)"}
            </span>{" "}
            — <span data-testid="backend-unreachable-reason">{state.reason}</span>.
          </div>
          <div className="text-rose-100/85 mt-1 text-[13px]">
            You can still browse free simulators, but sign-in, saved scores, and payments won&apos;t work.
            If this persists, contact Emergent Support and quote this URL.
          </div>
        </div>
        <button
          type="button"
          onClick={() => runCheck()}
          disabled={busy}
          className="ml-2 -mt-0.5 px-2.5 py-1 rounded bg-rose-800/60 hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed shrink-0 inline-flex items-center gap-1.5 text-[13px]"
          data-testid="backend-unreachable-retry"
          aria-label="Retry backend connection"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Checking…" : "Retry"}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="ml-1 -mr-1 -mt-0.5 p-1.5 rounded hover:bg-rose-700 shrink-0"
          data-testid="backend-unreachable-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default BackendHealthBanner;
